/**
 * The recording's `sourceId` for a Discogs track, minted the way the data
 * platform mints it.
 *
 * Source of truth: `_build_track_id` in
 * `opensearch/opensearch/parsers/discogs/releases.py` of functions-mono at
 * dfd67f0 — `TrackId:<release_id>:<n>`, with `-<m>` appended for a sub-track,
 * where `n` and `m` are 1-based positions in the **raw** Discogs tracklist and
 * sub-track list respectively. The backend's `discogs/release-tracklist/`
 * endpoint hands out exactly those raw indices (a heading or a title-less
 * entry still counts), so this module only has to format them. Change the
 * format here and DS will no longer recognise the recording as one it has.
 *
 * Pure and DOM-less, like the op builders beside it, so the id rule is testable
 * without rendering the form.
 */
import type { DiscogsReleaseTracklist } from 'shared_components/generated/admin';

export const buildDiscogsTrackId = (
  releaseId: string,
  trackIndex: number,
  subtrackIndex?: number
): string =>
  `TrackId:${releaseId}:${trackIndex}` +
  (subtrackIndex === undefined ? '' : `-${subtrackIndex}`);

/** The parts of a minted Discogs track id, or `null` for anything else. */
export interface DiscogsTrackIdParts {
  releaseId: string;
  trackIndex: number;
  subtrackIndex?: number;
}

const DISCOGS_TRACK_ID_RE = /^TrackId:(\d+):(\d+)(?:-(\d+))?$/;

/**
 * Read a minted id back into its parts — the inverse of `buildDiscogsTrackId`.
 *
 * The one consumer is the release pin: a Discogs recording can only be put on
 * the release it was picked from, and the picked release is the only thing the
 * form has that says which release that was. Read off the minted id rather
 * than the typed release id so the pin follows the id the backend canonicalised
 * (Discogs' numeric id), not whatever form the operator typed.
 */
export const parseDiscogsTrackId = (
  sourceId: string
): DiscogsTrackIdParts | null => {
  const match = DISCOGS_TRACK_ID_RE.exec(sourceId.trim());
  if (!match) return null;
  const [, releaseId, track, subtrack] = match;
  return {
    releaseId,
    trackIndex: Number(track),
    ...(subtrack === undefined ? {} : { subtrackIndex: Number(subtrack) }),
  };
};

export interface DiscogsTrackOption {
  /** The minted id — what the form stores as the recording's `sourceId`. */
  sourceId: string;
  label: string;
}

export interface DiscogsTrackOptionGroup {
  /** The heading (or index track) the options sit under; `null` for none. */
  label: string | null;
  options: DiscogsTrackOption[];
}

/** `A1  Title`, or just the title for an entry Discogs gives no position. */
const positionedTitle = (entry: { position: string; title: string }): string =>
  entry.position ? `${entry.position}  ${entry.title}` : entry.title;

const optionLabel = (entry: {
  position: string;
  title: string;
  duration: string;
}): string =>
  entry.duration
    ? `${positionedTitle(entry)} (${entry.duration})`
    : positionedTitle(entry);

/**
 * The tracklist as `<optgroup>`s: plain tracks grouped under the heading that
 * precedes them, and each index track a group of its own sub-tracks. Headings
 * and index tracks are never options themselves — DS mints no recording for a
 * heading, and an index track is a container whose sub-tracks are the
 * recordings. Empty groups are dropped.
 */
export const discogsTrackOptionGroups = (
  tracklist: DiscogsReleaseTracklist
): DiscogsTrackOptionGroup[] => {
  const groups: DiscogsTrackOptionGroup[] = [];
  let heading: string | null = null;
  let current: DiscogsTrackOptionGroup | null = null;

  for (const entry of tracklist.tracks) {
    if (entry.kind === 'heading') {
      heading = entry.title;
      current = null;
      continue;
    }
    if (entry.kind === 'index') {
      groups.push({
        label: positionedTitle(entry),
        options: entry.subtracks.map((sub) => ({
          sourceId: buildDiscogsTrackId(
            tracklist.releaseId,
            entry.index,
            sub.index
          ),
          label: optionLabel(sub),
        })),
      });
      // Anything after the index track starts a fresh group under the same
      // heading, so it does not read as a sub-track of the index.
      current = null;
      continue;
    }
    if (!current) {
      current = { label: heading, options: [] };
      groups.push(current);
    }
    current.options.push({
      sourceId: buildDiscogsTrackId(tracklist.releaseId, entry.index),
      label: optionLabel(entry),
    });
  }

  return groups.filter((g) => g.options.length > 0);
};
